import { digitsOnlyFromLoosePhoneCell } from './customerCsv'

export const CUSTOMER_IMPORT_REQUIRED_HEADERS = ['이름', '생년월일', '주소', '핸드폰', '메모']

export const CUSTOMER_SPREADSHEET_EXPORT_HEADERS = [
  '이름',
  '핸드폰',
  '고객 구분',
  '생년월일',
  '지역',
  '소속',
  '호칭',
  '주소',
  '회사 주소',
  '이메일',
  '고객 출처',
  '메모',
]

function exactHeaderCell(value) {
  return String(value ?? '').replace(/^\uFEFF/, '')
}

export function validateCustomerImportHeaders(headers) {
  const exactHeaders = headers.map(exactHeaderCell)
  const duplicates = CUSTOMER_IMPORT_REQUIRED_HEADERS.filter(
    (header) => exactHeaders.filter((candidate) => candidate === header).length > 1,
  )
  if (duplicates.length > 0) {
    return {
      ok: false,
      duplicates,
      missing: [],
      message: `첫 번째 행에 동일한 필수 컬럼이 중복되어 있습니다: ${duplicates.join(', ')}`,
    }
  }

  const missing = CUSTOMER_IMPORT_REQUIRED_HEADERS.filter((header) => !exactHeaders.includes(header))
  if (missing.length > 0) {
    return {
      ok: false,
      duplicates: [],
      missing,
      message: `첫 번째 행에 다음 컬럼명이 정확히 필요합니다: ${CUSTOMER_IMPORT_REQUIRED_HEADERS.join(', ')}. 누락: ${missing.join(', ')}`,
    }
  }
  return { ok: true, duplicates: [], missing: [], message: '' }
}

function detectDelimiter(text) {
  const firstLine = String(text || '').split(/\r\n|\n|\r/, 1)[0]
  const counts = [
    { delimiter: ',', count: (firstLine.match(/,/g) || []).length },
    { delimiter: '\t', count: (firstLine.match(/\t/g) || []).length },
    { delimiter: ';', count: (firstLine.match(/;/g) || []).length },
  ]
  counts.sort((left, right) => right.count - left.count)
  return counts[0].count > 0 ? counts[0].delimiter : ','
}

function parseDelimitedText(text, delimiter) {
  const rows = []
  let row = []
  let cell = ''
  let index = 0
  let inQuotes = false

  const pushCell = () => {
    row.push(cell)
    cell = ''
  }
  const pushRow = () => {
    rows.push(row)
    row = []
  }

  while (index < text.length) {
    const current = text[index]
    if (inQuotes) {
      if (current === '"') {
        if (text[index + 1] === '"') {
          cell += '"'
          index += 2
          continue
        }
        inQuotes = false
        index += 1
        continue
      }
      cell += current
      index += 1
      continue
    }

    if (current === '"') {
      inQuotes = true
      index += 1
      continue
    }
    if (current === delimiter) {
      pushCell()
      index += 1
      continue
    }
    if (current === '\r' || current === '\n') {
      if (current === '\r' && text[index + 1] === '\n') index += 1
      pushCell()
      pushRow()
      index += 1
      continue
    }
    cell += current
    index += 1
  }

  pushCell()
  if (row.length > 1 || row[0] !== '') pushRow()
  return rows
}

export function parseCustomerImportCsv(text) {
  const source = String(text || '').replace(/^\uFEFF/, '')
  const rows = parseDelimitedText(source, detectDelimiter(source))
  while (rows.length > 0 && rows[rows.length - 1].every((value) => String(value || '').trim() === '')) {
    rows.pop()
  }
  if (rows.length === 0) return { headers: [], rows: [] }

  return {
    headers: rows[0].map(exactHeaderCell),
    rows: rows.slice(1).filter((candidate) => candidate.some((value) => String(value || '').trim() !== '')),
  }
}

export function mapCustomerImportRow(headers, cells) {
  const values = {}
  headers.forEach((header, index) => {
    if (CUSTOMER_IMPORT_REQUIRED_HEADERS.includes(header)) {
      values[header] = cells[index] == null ? '' : String(cells[index])
    }
  })

  return {
    customerName: String(values.이름 || '').trim(),
    birthDateRaw: String(values.생년월일 || '').trim(),
    homeAddress: String(values.주소 || '').trim(),
    customerPhone: digitsOnlyFromLoosePhoneCell(values.핸드폰),
    memo: String(values.메모 || '').trim(),
  }
}

export async function readCustomerImportCsvFileAsText(file) {
  const buffer = await file.arrayBuffer()
  const decode = (encoding) => {
    try {
      return new TextDecoder(encoding, { fatal: false }).decode(buffer)
    } catch {
      return ''
    }
  }

  const utf8 = decode('utf-8')
  if (validateCustomerImportHeaders(parseCustomerImportCsv(utf8).headers).ok) return utf8

  for (const encoding of ['windows-949', 'euc-kr']) {
    const decoded = decode(encoding)
    if (decoded && validateCustomerImportHeaders(parseCustomerImportCsv(decoded).headers).ok) {
      return decoded
    }
  }
  return utf8
}
